import { Link } from 'react-router-dom';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <nav className="p-6 flex justify-between items-center max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-indigo-600">EmbedZoom</h1>
        <div className="space-x-4">
          <Link
            to="/login"
            className="px-4 py-2 text-indigo-600 hover:text-indigo-700"
          >
            Login
          </Link>
          <Link
            to="/register"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Get Started
          </Link>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Embed Zoom Meetings
            <br />
            <span className="text-indigo-600">Into Any Webpage</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            The easiest way to add Zoom meetings and webinars to your website.
            No WordPress required. Just copy, paste, and go.
          </p>
          <div className="space-x-4">
            <Link
              to="/register"
              className="inline-block px-8 py-4 bg-indigo-600 text-white text-lg rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Start Free Trial
            </Link>
            <a
              href="#how-it-works"
              className="inline-block px-8 py-4 border-2 border-indigo-600 text-indigo-600 text-lg rounded-lg hover:bg-indigo-50 transition-colors"
            >
              See How It Works
            </a>
          </div>
        </div>

        {/* Features */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">Quick Setup</h3>
            <p className="text-gray-600">
              Connect your Zoom account and start embedding meetings in minutes. No coding required.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">Customizable</h3>
            <p className="text-gray-600">
              Match your brand with custom colors, themes, and styling options. Make it yours.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">Analytics</h3>
            <p className="text-gray-600">
              Track views, participants, and engagement metrics. Know your audience.
            </p>
          </div>
        </div>

        {/* How It Works */}
        <div id="how-it-works" className="mt-24">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">1</div>
              <h4 className="font-semibold mb-2">Connect Zoom</h4>
              <p className="text-gray-600 text-sm">Link your Zoom account with one click</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">2</div>
              <h4 className="font-semibold mb-2">Create Meeting</h4>
              <p className="text-gray-600 text-sm">Schedule or import existing meetings</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">3</div>
              <h4 className="font-semibold mb-2">Customize</h4>
              <p className="text-gray-600 text-sm">Style the embed to match your brand</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">4</div>
              <h4 className="font-semibold mb-2">Embed</h4>
              <p className="text-gray-600 text-sm">Copy code and paste into any website</p>
            </div>
          </div>
        </div>

        {/* Pricing Preview */}
        <div className="mt-24 text-center">
          <h2 className="text-3xl font-bold mb-4">Simple, Transparent Pricing</h2>
          <p className="text-gray-600 mb-8">Start free, upgrade when you need more</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="font-bold text-lg mb-2">Free</h3>
              <p className="text-3xl font-bold mb-4">$0<span className="text-sm text-gray-500">/mo</span></p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li>5 embedded meetings</li>
                <li>Basic analytics</li>
                <li>Standard support</li>
              </ul>
              <Link to="/register" className="block w-full py-2 border border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50">
                Get Started
              </Link>
            </div>
            <div className="bg-indigo-600 text-white p-6 rounded-xl shadow-lg transform scale-105">
              <h3 className="font-bold text-lg mb-2">Pro</h3>
              <p className="text-3xl font-bold mb-4">$29<span className="text-sm opacity-75">/mo</span></p>
              <ul className="text-sm opacity-90 space-y-2 mb-6">
                <li>Unlimited meetings</li>
                <li>Advanced analytics</li>
                <li>Custom branding</li>
                <li>Priority support</li>
              </ul>
              <Link to="/register" className="block w-full py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50">
                Start Free Trial
              </Link>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="font-bold text-lg mb-2">Business</h3>
              <p className="text-3xl font-bold mb-4">$99<span className="text-sm text-gray-500">/mo</span></p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li>Everything in Pro</li>
                <li>Team accounts</li>
                <li>White-label</li>
                <li>API access</li>
              </ul>
              <Link to="/register" className="block w-full py-2 border border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-24 bg-indigo-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-lg opacity-90 mb-8">Join thousands of users embedding Zoom meetings on their websites</p>
          <Link
            to="/register"
            className="inline-block px-8 py-4 bg-white text-indigo-600 text-lg font-semibold rounded-lg hover:bg-indigo-50 transition-colors"
          >
            Create Free Account
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 mt-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-white font-bold text-lg mb-4">EmbedZoom</h3>
              <p className="text-sm">Embed Zoom meetings into any webpage. Simple, powerful, customizable.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Documentation</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">About</a></li>
                <li><a href="#" className="hover:text-white">Blog</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-sm text-center">
            <p>&copy; {new Date().getFullYear()} EmbedZoom. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
